import type { ReactNode } from 'react';
import { useMemo } from 'react';
import styled from 'styled-components';

import type { BoxProps } from '@hivebrite/design-system/Box';
import { Box } from '@hivebrite/design-system/Box';
import { Dotdotdot } from '@hivebrite/design-system/Dotdotdot';
import { FlagStatus, STATUS } from '@hivebrite/design-system/Flag';
import {
  CATEGORIES,
  FontAwesomeIcon,
} from '@hivebrite/design-system/FontAwesomeIcon';
import { Image } from '@hivebrite/design-system/Image';
import { PillsList } from '@hivebrite/design-system/PillsList';
import { ScreenReadersContent } from '@hivebrite/design-system/ScreenReadersContent';

import { Calendar as CalendarComponent } from 'hb-react/shared/apps/Events/components/Calendar';
import EventPricingTag from 'hb-react/shared/apps/Events/components/EventPricingTag';
import EventDate from 'hb-react/shared/components/EventDate';
import useTimeZoneConfiguration from 'hb-react/shared/hooks/useTimeZoneConfiguration';
import type { TimeZoneConfiguration } from 'hb-react/types/timeZoneConfiguration';

import type { Event } from '../types';

const DEFAULT_MISSING_TOPIC_IMAGE_PATH =
  '/images/missing/topics/logo/default.jpg';

const getValidAvatarUrl = (avatarUrl?: string): string | null =>
  avatarUrl && !avatarUrl.includes(DEFAULT_MISSING_TOPIC_IMAGE_PATH)
    ? avatarUrl
    : null;

export const Owner = ({ event }: { event: Pick<Event, 'parent'> }) => {
  const hideForNetwork = event?.parent?.parentType === 'Network';

  if (!event.parent || hideForNetwork) {
    return null;
  }

  const validAvatarUrl = getValidAvatarUrl(event.parent.avatarUrl);

  return (
    <Box alignItems="center">
      {validAvatarUrl ? (
        <AvatarBox $parentType={event.parent.parentType}>
          <AvatarImage src={validAvatarUrl} />
        </AvatarBox>
      ) : (
        <FontAwesomeIcon
          iconName="user-group"
          iconType={CATEGORIES.SOLID}
          color="asphalt"
          height="10px"
        />
      )}
      <Box ml={2} font="body-semibold-asphalt">
        {event.parent.name}
      </Box>
    </Box>
  );
};

const AvatarBox = styled(Box).attrs({
  flexShrink: 0,
  overflow: 'hidden',
  justifyContent: 'center',
  alignItems: 'center',
})<{ $parentType?: string }>`
  border-radius: ${({ $parentType }) =>
    $parentType === 'User' ? '50%' : '5px'};
`;

const AvatarImage = styled(Image).attrs({
  alt: '',
  role: 'presentation',
})`
  width: 24px;
  height: 24px;
  object-fit: cover;
`;

export const Cancelled = ({ event }: { event: Event }) => {
  if (!event.cancelled) {
    return null;
  }

  return (
    <Box position="absolute" top="8px" left="8px">
      <FlagStatus status={STATUS.ERROR} text={__('Canceled')} />
    </Box>
  );
};

export const PricingTag = ({ event }: { event: Event }) => {
  const eventProps = useMemo(
    () => ({
      id: event.id,
      display_price_tag: event.displayPriceTag,
      tickets_due_date: event.ticketsDueDate,
      participation_type: event.participationType,
    }),
    [
      event.id,
      event.displayPriceTag,
      event.ticketsDueDate,
      event.participationType,
    ],
  );

  return (
    <Box position="absolute" top="8px" left="8px">
      <EventPricingTag event={eventProps} />
    </Box>
  );
};

export const Calendar = ({
  startDate,
  timezoneConfiguration,
  eventTimezone,
}: {
  startDate: string;
  timezoneConfiguration?: TimeZoneConfiguration;
  eventTimezone: string;
}) => {
  const { currentUserTimezone, eventDatesInUserTimezone } =
    timezoneConfiguration || {};

  const eventDisplayTimeZone =
    eventDatesInUserTimezone && currentUserTimezone
      ? currentUserTimezone
      : eventTimezone;

  return (
    <Box position="absolute" left="8px" bottom="5px">
      <CalendarComponent
        startDate={startDate}
        eventDisplayTimeZone={eventDisplayTimeZone}
      />
    </Box>
  );
};

export const Header = styled(Box).attrs({
  overflow: 'hidden',
  position: 'relative',
  width: '100%',
})``;

export const Body = ({ children }: { children: ReactNode }) => (
  <Box
    display="flex"
    flex={1}
    flexDirection="column"
    justifyContent="space-between"
    pt={2}
    px={3}
    pb={4}
    gridRowGap={2}
    minHeight="200px"
  >
    {children}
  </Box>
);

export const Title = styled(Box).attrs({
  font: 'h3-semibold',
})`
  word-break: break-word;
`;

export const StartEndDates = ({
  event,
}: {
  event: Pick<Event, 'startDate' | 'endDate' | 'timezone'>;
}) => {
  const { currentUserTimezone } = useTimeZoneConfiguration();

  const eventProps = useMemo(
    () => ({
      start_date: event.startDate,
      end_date: event.endDate,
      timezone: event.timezone,
    }),
    [event.startDate, event.endDate, event.timezone],
  );

  return (
    <Box display="block">
      <EventDate
        event={eventProps}
        timezoneConfiguration={{
          currentUserTimezone,
        }}
      />
    </Box>
  );
};

export const Location = ({ event }: { event: Pick<Event, 'location'> }) => {
  const displayLocation = event.location?.location ?? event.location?.address;

  if (!displayLocation) {
    return null;
  }

  return (
    <Box gridColumnGap={1} alignItems="center">
      <FontAwesomeIcon
        height="12px"
        iconName="location-dot"
        iconType={CATEGORIES.SOLID}
      />
      <Dotdotdot clamp={2}>{displayLocation}</Dotdotdot>
    </Box>
  );
};

export const OnlineEvent = ({
  event,
}: {
  event: Pick<Event, 'webinarUrl' | 'isInternalZoomMeeting'>;
}) => {
  const isOnline = event.webinarUrl || event.isInternalZoomMeeting;

  if (!isOnline) {
    return null;
  }

  return (
    <Box gridColumnGap={1} alignItems="center">
      <FontAwesomeIcon
        height="12px"
        iconName="video"
        iconType={CATEGORIES.SOLID}
      />
      <Box>{__('Online event')}</Box>
    </Box>
  );
};

export const Categories = ({
  event,
  ...props
}: {
  event: Pick<Event, 'networkCategories'>;
} & BoxProps) => {
  if (!event.networkCategories?.length) {
    return null;
  }

  return (
    <Box justifyContent="flex-end" {...props}>
      <ScreenReadersContent>{__('Tags')}:</ScreenReadersContent>
      <PillsList
        pills={event.networkCategories}
        seeMoreLabel={__('See More')}
        seeLessLabel={s__('Tags|See less')}
      />
    </Box>
  );
};
